import { Check } from 'lucide-react';

/**
 * One quiz question — block eyebrow, question text, and the type-specific
 * control (single radio-cards, multi checkbox-cards, or a text area).
 * Fully controlled: `value` shape is `{value}` / `{values, otherText}` /
 * `{text}` per question.type — see src/utils/quizAnswers.js for validation.
 */
export default function QuizQuestion({ question, value, onChange }) {
  const isSingle = question.type === 'single';
  const isMulti = question.type === 'multi';
  const isText = question.type === 'text';

  const selectedSingle = isSingle ? value?.value ?? null : null;
  const selectedMulti = isMulti ? value?.values ?? [] : [];
  const otherText = value?.otherText ?? '';
  const showOtherInput =
    question.allowOther &&
    ((isSingle && selectedSingle === 'other') || (isMulti && selectedMulti.includes('other')));

  const handleSingleSelect = (optionValue) => {
    const result = { value: optionValue };
    if (optionValue === 'other' && otherText) {
      result.otherText = otherText;
    }
    onChange(result);
  };

  const handleMultiToggle = (optionValue) => {
    const isSelected = selectedMulti.includes(optionValue);
    if (!isSelected && question.maxSelect && selectedMulti.length >= question.maxSelect) return;
    const next = isSelected
      ? selectedMulti.filter((v) => v !== optionValue)
      : [...selectedMulti, optionValue];
    const result = { values: next };
    if (next.includes('other') && otherText) {
      result.otherText = otherText;
    }
    onChange(result);
  };

  const handleOtherTextChange = (text) => {
    if (isSingle) onChange({ value: selectedSingle, otherText: text });
    else if (isMulti) onChange({ values: selectedMulti, otherText: text });
  };

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{question.block}</p>
      <h2 className="text-xl font-bold text-[#1E293B] mb-6">{question.question}</h2>

      <div className="space-y-3">
        {isSingle &&
          question.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSingleSelect(opt.value)}
              className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-colors ${
                selectedSingle === opt.value
                  ? 'border-[#F47920] bg-orange-50 text-[#1E293B] font-semibold'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              {opt.label}
            </button>
          ))}

        {isMulti &&
          question.options.map((opt) => {
            const isSelected = selectedMulti.includes(opt.value);
            const isDisabled = !isSelected && question.maxSelect && selectedMulti.length >= question.maxSelect;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={isDisabled}
                onClick={() => handleMultiToggle(opt.value)}
                className={`w-full flex items-center gap-3 text-left px-5 py-4 rounded-2xl border-2 transition-colors ${
                  isSelected
                    ? 'border-[#F47920] bg-orange-50 text-[#1E293B] font-semibold'
                    : isDisabled
                    ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-[#F47920] bg-[#F47920]' : 'border-gray-300'
                  }`}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                </span>
                {opt.label}
              </button>
            );
          })}

        {isText && (
          <textarea
            value={value?.text ?? ''}
            onChange={(e) => onChange({ text: e.target.value })}
            rows={5}
            placeholder="Your answer (optional)"
            className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#F47920]"
          />
        )}

        {showOtherInput && (
          <input
            type="text"
            value={otherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder="Please specify"
            className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#F47920]"
          />
        )}
      </div>
    </div>
  );
}
